import {Injectable} from '@angular/core';
import {KenBurnsEffect} from '../../shared/components/slide-show/models/ken-burns-effect';

@Injectable({
  providedIn: 'root'
})
export class KenBurnsService {
  private readonly effectTypes: KenBurnsEffect['type'][] = [
    'zoom-in',
    'zoom-out',
    'pan-left',
    'pan-right',
    'pan-up',
    'pan-down'
  ];

  private readonly defaultDurationRange = {
    min: 5000,  // 5 seconds
    max: 8000   // 8 seconds
  };

  /**
   * Generate Ken Burns effects for a collection of media items based on their types and durations
   * @param mediaItems Array of media items with type and duration
   * @param durations Array of calculated durations for each media item
   * @returns Array of KenBurnsEffect objects
   */
  generateEffectsForMediaItems(mediaItems: {
    type: string;
    duration: number
  }[], durations: number[]): KenBurnsEffect[] {
    const effects: KenBurnsEffect[] = [];

    mediaItems.forEach((item, index) => {
      // Get the appropriate duration from the provided array
      const duration = durations[index];

      // Select effect type based on position and media type
      let effectType: KenBurnsEffect['type'];

      // Avoid repeating the same effect type consecutively
      if (effects.length > 0) {
        const lastType = effects[effects.length - 1].type;
        const availableTypes = this.effectTypes.filter(type => type !== lastType);
        effectType = this.getRandomItem(availableTypes);
      } else {
        effectType = this.getRandomItem(this.effectTypes);
      }

      // Create the effect with appropriate defaults based on type
      const effect = this.createEffectByType(effectType, duration);
      effects.push(effect);
    });

    return effects;
  }

  /**
   * Generate Ken Burns effects for a collection of media items
   * @param count Number of effects to generate
   * @param options Configuration options
   * @returns Array of KenBurnsEffect objects
   */
  generateEffects(count: number, options?: {
    durationRange?: { min: number; max: number };
    preferredTypes?: KenBurnsEffect['type'][];
    randomizeOrder?: boolean;
  }): KenBurnsEffect[] {
    const durationRange = options?.durationRange || this.defaultDurationRange;
    const effectTypes = options?.preferredTypes || this.effectTypes;
    const randomizeOrder = options?.randomizeOrder !== undefined ? options.randomizeOrder : true;

    const effects: KenBurnsEffect[] = [];

    for (let i = 0; i < count; i++) {
      // Ensure we don't repeat the same effect type consecutively
      let effectType: KenBurnsEffect['type'];
      if (effects.length > 0) {
        const lastType = effects[effects.length - 1].type;
        const availableTypes = effectTypes.filter(type => type !== lastType);
        effectType = this.getRandomItem(availableTypes);
      } else {
        effectType = this.getRandomItem(effectTypes);
      }

      // Generate a natural duration with slight variations
      const duration = this.getRandomInRange(durationRange.min, durationRange.max);

      // Create the effect with appropriate defaults based on type
      const effect = this.createEffectByType(effectType, duration);

      effects.push(effect);
    }

    // Optionally randomize the order
    if (randomizeOrder) {
      this.shuffleArray(effects);
    }

    return effects;
  }

  /**
   * Apply a Ken Burns effect to an HTML element via CSS
   * @param element The element to apply the effect to
   * @param effect The Ken Burns effect to apply
   */
  applyEffect(element: HTMLElement, effect: KenBurnsEffect): void {
    // Ensure image is properly sized for blur background layout
    element.style.objectFit = 'contain';
    element.style.maxWidth = '100%';
    element.style.maxHeight = '100%';

    // Apply the appropriate transform based on effect type
    switch (effect.type) {
      case 'zoom-in':
        this.applyZoomInEffect(element, effect);
        break;
      case 'zoom-out':
        this.applyZoomOutEffect(element, effect);
        break;
      case 'pan-left':
      case 'pan-right':
      case 'pan-up':
      case 'pan-down':
        // Calculate transform for panning effects
        this.applyPanEffect(element, effect);
        break;
    }
  }

  /**
   * Create a Ken Burns effect by type
   * @param type The type of effect to create
   * @param duration The duration of the effect
   * @returns A Ken Burns effect object
   */
  private createEffectByType(type: KenBurnsEffect['type'], duration: number): KenBurnsEffect {
    switch (type) {
      case 'zoom-in':
        return {
          type,
          duration,
          startScale: 1,
          endScale: 1.2,
          easing: 'ease-in-out'
        };
      case 'zoom-out':
        return {
          type,
          duration,
          startScale: 1.2,
          endScale: 1,
          easing: 'ease-in-out'
        };
      case 'pan-left':
        return {
          type,
          duration,
          // Use more subtle position values (0.4-0.6 range instead of 0-1)
          startPosition: {x: 0.6, y: 0.5},
          endPosition: {x: 0.4, y: 0.5},
          scale: 1.1, // Slight zoom to prevent empty edges
          easing: 'ease-in-out'
        };
      case 'pan-right':
        return {
          type,
          duration,
          startPosition: {x: 0.4, y: 0.5},
          endPosition: {x: 0.6, y: 0.5},
          scale: 1.1,
          easing: 'ease-in-out'
        };
      case 'pan-up':
        return {
          type,
          duration,
          startPosition: {x: 0.5, y: 0.6},
          endPosition: {x: 0.5, y: 0.4},
          scale: 1.1,
          easing: 'ease-in-out'
        };
      case 'pan-down':
        return {
          type,
          duration,
          startPosition: {x: 0.5, y: 0.4},
          endPosition: {x: 0.5, y: 0.6},
          scale: 1.1,
          easing: 'ease-in-out'
        };
      default:
        return {
          type: 'zoom-in',
          duration,
          startScale: 1,
          endScale: 1.2,
          easing: 'ease-in-out'
        };
    }
  }

  /**
   * Apply a zoom-in effect to an element
   * @param element The element to apply the effect to
   * @param effect The Ken Burns effect to apply
   */
  private applyZoomInEffect(element: HTMLElement, effect: KenBurnsEffect): void {
    // Set the transform origin to center (default)
    element.style.transformOrigin = 'center center';

    // Set the initial transform
    element.style.transform = `scale(${effect.startScale || 1})`;

    // Force a reflow to ensure the initial transform takes effect
    void element.offsetWidth;

    // Set up the transition
    element.style.transition = `transform ${effect.duration}ms ${effect.easing || 'ease-in-out'}`;

    // Apply the final transform
    setTimeout(() => {
      element.style.transform = `scale(${effect.endScale || 1.2})`;
    }, 50);
  }

  /**
   * Apply a zoom-out effect to an element
   * @param element The element to apply the effect to
   * @param effect The Ken Burns effect to apply
   */
  private applyZoomOutEffect(element: HTMLElement, effect: KenBurnsEffect): void {
    // Set the transform origin to center (default)
    element.style.transformOrigin = 'center center';

    // Set the initial transform
    element.style.transform = `scale(${effect.startScale || 1.2})`;

    // Force a reflow to ensure the initial transform takes effect
    void element.offsetWidth;

    // Set up the transition
    element.style.transition = `transform ${effect.duration}ms ${effect.easing || 'ease-in-out'}`;

    // Apply the final transform
    setTimeout(() => {
      element.style.transform = `scale(${effect.endScale || 1})`;
    }, 50);
  }

  /**
   * Apply a panning effect to an element
   * @param element The element to apply the effect to
   * @param effect The Ken Burns effect to apply
   */
  private applyPanEffect(element: HTMLElement, effect: KenBurnsEffect): void {
    // Ensure image is properly sized for blur background layout
    element.style.objectFit = 'contain';
    element.style.maxWidth = '100%';
    element.style.maxHeight = '100%';

    // Use more subtle values for panning (reduce movement range)
    // Convert normalized positions (0-1) to percentage values (-10% to 10%)
    const startX = ((effect.startPosition?.x || 0.5) - 0.5) * 20;
    const startY = ((effect.startPosition?.y || 0.5) - 0.5) * 20;
    const endX = ((effect.endPosition?.x || 0.5) - 0.5) * 20;
    const endY = ((effect.endPosition?.y || 0.5) - 0.5) * 20;

    // Set the transform origin to center (default)
    element.style.transformOrigin = 'center center';

    // For panning, we want to move from the end position to the start position
    // This creates the illusion of the viewport moving across the image
    // Use a scale factor to ensure the image remains larger than the container
    const scale = effect.scale || 1.1; // Slight zoom to prevent empty edges

    // Apply the initial transform (end position)
    element.style.transform = `scale(${scale}) translate(${-endX}%, ${-endY}%)`;

    // Force a reflow to ensure the initial transform takes effect
    void element.offsetWidth;

    // Set up the transition
    element.style.transition = `transform ${effect.duration}ms ${effect.easing || 'ease-in-out'}`;

    // Apply the final transform (start position)
    setTimeout(() => {
      element.style.transform = `scale(${scale}) translate(${-startX}%, ${-startY}%)`;
    }, 50);
  }

  /**
   * Get a random item from an array
   * @param array The array to get a random item from
   * @returns A random item from the array
   */
  private getRandomItem<T>(array: T[]): T {
    return array[Math.floor(Math.random() * array.length)];
  }

  /**
   * Get a random number in a range
   * @param min Minimum value
   * @param max Maximum value
   * @returns A random number between min and max
   */
  private getRandomInRange(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1) + min);
  }

  /**
   * Shuffle an array in place
   * @param array The array to shuffle
   */
  private shuffleArray<T>(array: T[]): void {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
  }
}
